<template>
    <auth-layout>
        <template #heading>
            <h1 class="mb-6 text-4xl font-bold md:text-6xl">Welcome Back</h1>
            <p class="text-lg">Please sign in to gain Access Beyond the Ordinary™️.</p>
        </template>

        <app-errors v-if="!form.verified" :form="form" class="mb-6"></app-errors>

        <h2 class="mb-4 text-2xl font-bold text-black">Login</h2>

        <div class="space-y-4">
            <text-field v-model="form.email" name="email" label="Email Address" :required="true" @keyup.enter="handleLogin"></text-field>

            <text-field v-model="form.password" type="password" name="password" label="Password" :required="true" @keyup.enter="handleLogin"></text-field>

            <checkbox-field v-model="form.remember">
                <span class="text-base text-gray-400">Remember me</span>
            </checkbox-field>

            <checkbox-field v-model="form.terms">
                <span class="text-base font-medium text-gray-400">By clicking Login, I agree and accept the <a href="https://mammoth.vc/terms/" class="font-bold text-black" target="_blank">Terms of Service</a>.</span>
            </checkbox-field>
        </div>

        <div class="flex justify-end space-x-6 mt-14">
            <app-button @click="handleBack" class="!border-gray-200 !bg-white !text-gray-400">Back</app-button>

            <app-button @click="handleLogin()" :busy="form.busy">Next</app-button>
        </div>

        <div class="mt-14">
            <p class="text-lg text-gray-400">Forgot password? <a href="#" @click.prevent="forgotForm.show = true" class="font-bold text-black">Reset here</a></p>
        </div>

        <app-modal :show="form.verified || form.resend" :closeable="true" @close="closeModals" class="sm:max-w-2xl">
            <div class="px-5 py-6 bg-white rounded-md shadow-sm sm:px-20 sm:py-10">
                <app-errors :form="form" class="mb-6"></app-errors>

                <h2 class="mb-1 text-2xl font-bold text-black">Enter Verification Code</h2>
                <p class="mb-6 text-lg text-gray-400">Enter the 6 digit code that we send to your email</p>

                <div class="mb-6">
                    <codes-field v-model="form.code" label="Verification Code" :required="true"></codes-field>
                </div>

                <div class="flex justify-end mb-6">
                    <app-button @click="handleVerification" :busy="form.busy">Continue</app-button>
                </div>

                <p v-show="!form.resend" class="text-lg text-gray-400">Didn’t received the code? <a href="#" @click.prevent="handleResend" class="text-black">Resend code</a></p>
                <p v-show="form.resend" class="text-lg text-green-800">Verification has been sent!</p>
            </div>
        </app-modal>

        <app-modal :show="forgotForm.show" :closeable="true" @close="closeModals" class="sm:max-w-2xl">
            <div class="px-5 py-6 bg-white rounded-md shadow-sm sm:px-20 sm:py-10">
                <div v-if="!forgotForm.successful">
                    <app-errors :form="forgotForm" class="mb-6"></app-errors>

                    <h2 class="mb-1 text-2xl font-bold text-black">Forgot Password</h2>
                    <p class="mb-6 text-lg text-gray-400">Enter your account email to recover your password</p>

                    <div class="mb-6">
                        <text-field v-model="forgotForm.email" label="Email Address" :required="true"></text-field>
                    </div>

                    <div class="flex justify-end mb-6">
                        <app-button @click="handleResetLink" :busy="forgotForm.busy">Submit</app-button>
                    </div>
                </div>
                <div v-else class="text-center">
                    <svg class="w-12 h-12 mx-auto mb-2 text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
                    <p class="text-xl font-medium">{{ forgotForm.message }}</p>
                </div>
            </div>
        </app-modal>

        <app-modal :show="resetForm.show" :closeable="true" @close="closeModals" class="sm:max-w-2xl">
            <div class="px-5 py-6 bg-white rounded-md shadow-sm sm:px-20 sm:py-10">
                <div v-if="!resetForm.successful">
                    <app-errors :form="resetForm" class="mb-6"></app-errors>

                    <h2 class="mb-1 text-2xl font-bold text-black">Reset Password</h2>
                    <p class="mb-6 text-lg text-gray-400">You can now set the new password for your account</p>

                    <div class="mb-6 space-y-6">
                        <text-field type="password" v-model="resetForm.password" label="Password" :required="true"></text-field>

                        <text-field type="password" v-model="resetForm.password_confirmation" label="Confirm Password" :required="true"></text-field>
                    </div>

                    <div class="flex justify-end mb-6">
                        <app-button @click="handleResetPassword" :busy="resetForm.busy">Reset Password</app-button>
                    </div>
                </div>
                <div v-else class="text-center">
                    <svg class="w-12 h-12 mx-auto mb-2 text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
                    <p class="text-xl font-medium">{{ resetForm.message }}</p>
                </div>
            </div>
        </app-modal>
    </auth-layout>
</template>

<script>
import AuthLayout from '@/Layouts/Auth'
import AppModal from '@Components/Modal'
import AppButton from '@Components/Button'
import AppErrors from '@Components/Errors'
import TextField from '@Fields/Text'
import CheckboxField from '@Fields/Checkbox'
import CodesField from '@Fields/Codes'
import { ref, onMounted, computed } from 'vue'
import useForm from '@/form'
import { usePage } from '@inertiajs/inertia-vue3'

export default {
    components: {
        AuthLayout,
        AppModal,
        AppButton,
        AppErrors,
        TextField,
        CheckboxField,
        CodesField
    },
    props: {
        loginData: {
            type: Object,
            default: () => ({})
        }
    },
    setup(props){
        const pagedata = computed(() => {
            return usePage().props.value
        })

        const isProduction = computed(() => {
            return _.get(pagedata.value, 'env', 'local') == 'production'
        })

        const verificationCode = ref('')

        const form = useForm({
            email: '',
            password: '',
            remember: false,
            terms: false,
            verified: false,
            resend: false,
            code: ''
        });

        const forgotForm = useForm({
            email: '',
            show: false,
            message: 'We have emailed your password reset link!'
        })

        const resetForm = useForm({
            token: '',
            email: '',
            password: '',
            password_confirmation: '',
            show: false,
            message: 'Successfully reset password'
        })

        onMounted(() => {
            if (_.has(props.loginData, 'code') && props.loginData.code != '') {
                form.verified = true;
                verificationCode.value = props.loginData.code;
            }

            const params = route().params;

            if (_.has(params, 'resetpass') && _.has(params, 'email')) {
                resetForm.show = true
                resetForm.token = params.resetpass;
                resetForm.email = params.email;
            }
        })

        function handleBack(){
            window.location.href = route('signup');
        }

        function handleLogin(){
            form.post(route('login')).then(response => {
                if (isProduction.value) {
                    form.verified = true
                    verificationCode.value = response.data
                } else {
                    window.location.href = route('index');
                }
            }).catch(errors => {
                window.scroll(0, 0);
            });
        }

        function handleVerification(){
            form.errors.clear()

            if (form.code == '' || form.code != verificationCode.value) {
                form.errors.set({
                    error: 'Invalid verification code'
                })

                return false;
            }

            form.verified = true;

            form.post(route('login')).then(response => {
                window.location.href = route('index');
            });
        }

        function handleResend(){
            form.resend = true
            form.verified = false
            handleLogin();
        }

        function handleResetLink(){
            forgotForm.post(route('password.email')).then(response => {
                console.log(response.data)
            });
        }

        function handleResetPassword(){
            resetForm.post(route('password.update')).then(response => {
                console.log(response.data)
            });
        }

        function closeModals(){
            form.reset(true)
            forgotForm.reset(true)
            resetForm.reset(true)
        }

        return {
            form,
            forgotForm,
            resetForm,
            handleBack,
            handleLogin,
            handleVerification,
            handleResend,
            handleResetLink,
            handleResetPassword,
            closeModals
        }
    }
}
</script>

<style>

</style>
